import { useQuery } from '@tanstack/react-query'
import * as yup from 'yup'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatMiddlewareDataFormProps } from '../../ChatMiddlewareDataFormProps'
import { Input } from '../../../Input'
import { ToolFormCreateCogfyRecord } from './_ToolFormCreateCogfyRecord'
import { ToolFormGetGoogleAuthUrl } from './_ToolFormGetGoogleAuthUrl'
import { ToolFormHttpRequest } from './_ToolFormHttpRequest'
import { ToolFormSearchCogfyRecords } from './_ToolFormSearchCogfyRecords'
import { ToolFormSendCogfyRecordFile } from './_ToolFormSendCogfyRecordFile'
import { ToolFormUpdateCogfyRecord } from './_ToolFormUpdateCogfyRecord'
import { useCogfy, useCollections } from '../../../../hooks'
import { AutogeneratedForm } from '../../../AutogeneratedForm'
import { OverlayContent } from '../../../OverlayContent'
import { SearchInput } from '../../../../pages/CollectionPageV2/components/SearchInput'
import { Menu } from '../../../Menu'
import { ToolFormFilteredMenu } from '../ToolFormFilteredMenu'
import { ToolFormClearCogfyRecordFields } from './_ToolFormClearCogfyRecordFields'

export const nameValidation = yup
  .string()
  .matches(/^[a-zA-Z0-9_-]*$/)

const TOOL_FORMS_BY_TYPE: Record<string, typeof ToolFormCreateCogfyRecord> = {
  clearCogfyRecordFields: ToolFormClearCogfyRecordFields,
  createCogfyRecord: ToolFormCreateCogfyRecord,
  getGoogleCalendarAuthUrl: ToolFormGetGoogleAuthUrl,
  httpRequest: ToolFormHttpRequest,
  searchCogfyRecords: ToolFormSearchCogfyRecords,
  sendCogfyRecordFile: ToolFormSendCogfyRecordFile,
  updateCogfyRecord: ToolFormUpdateCogfyRecord
}

const DISPLAYNAME_BY_INTERNAL_TOOL_NAME: Record<string, string> = {
  clearCogfyRecordFields: 'Clear Cogfy Record Fields',
  createCogfyRecord: 'Create Cogfy Record',
  searchCogfyRecords: 'Search Cogfy Record',
  sendCogfyRecordFile: 'Send Cogfy Record File',
  updateCogfyRecord: 'Update Cogfy Record'
}

export function ToolForm (props: ChatMiddlewareDataFormProps) {
  const { data, onChange } = props

  const { t } = useTranslation()
  const cogfy = useCogfy()
  const [name, setName] = useState(data.name ?? '')
  const [description, setDescription] = useState(data.description ?? '')

  const getEligibleCogfyCreditsProviders = useQuery({
    queryKey: ['getEligibleCogfyCreditsProviders', {}] as const,
    queryFn: ({ signal }) => cogfy.getEligibleCogfyCreditsProviders({ signal })
  })
  const getChatToolSchemas = useQuery({
    queryKey: ['getChatToolSchemas'],
    queryFn: ({ signal }) => cogfy.getChatToolSchemas({ signal })
  })
  const getConnections = useQuery({
    queryKey: ['getConnectionsList', {}] as const,
    queryFn: ({ queryKey, signal }) => cogfy.getConnectionsList(queryKey[1], { signal })
  })
  const getCollections = useCollections({ type: 'database' })
  const eligibleCogfyCreditsProviders = getEligibleCogfyCreditsProviders.data?.data ?? []
  const connections = getConnections.data?.data ?? []
  const collections = getCollections.data?.data ?? []
  const isNameValid = nameValidation.isValidSync(name)

  const toolNameOptions = [
    { label: 'Select a tool', value: '', displayName: '' },
    ...(getChatToolSchemas.data?.data.map(tool => ({
      label: tool.name,
      value: tool.name,
      displayName: tool.schema && 'displayName' in tool.schema
        ? tool.schema.displayName as string
        : (DISPLAYNAME_BY_INTERNAL_TOOL_NAME[tool.name] || tool.name)
    })) ?? [])
  ]

  // v2
  const [toolName, setToolName] = useState<string | null>(data.method?.name ?? null)
  const [toolData, setToolData] = useState<Record<string, any>>(data.method?.data ?? {})
  const toolSchema = getChatToolSchemas.data?.data.find(schema => schema.name === toolName)

  const ToolFormComponent = toolName ? TOOL_FORMS_BY_TYPE[toolName] : undefined

  const [selectOperationEl, setSelectOperationEl] = useState<HTMLDivElement | null>(null)
  const [searchOperationName, setSearchOperationName] = useState('')
  const [menuWidth, setMenuWidth] = useState(0)

  useEffect(() => {
    if (selectOperationEl) {
      setMenuWidth(selectOperationEl.getBoundingClientRect().width)
    }
  }, [selectOperationEl])

  return (
    <div className="flex flex-col gap-2">
      <Input
        label={t('editFieldDrawer:Tool name')}
        size="sm"
        value={name}
        onChange={event => {
          const newName = event.target.value
          setName(newName)
          if (nameValidation.isValidSync(newName)) onChange({ ...props.data, name: newName || null })
        }}
        error={!isNameValid && name.length > 0}
        helperText={!isNameValid && name.length > 0 ? t('editFieldDrawer:Name must contain only letters, numbers, _ or -') : ''}
      />

      <Input
        label={t('editFieldDrawer:Tool description')}
        size="sm"
        value={description}
        onChange={event => {
          setDescription(event.target.value)
          onChange({ ...props.data, description: event.target.value || null })
        }}
      />

      <div className="flex flex-col gap-2 text-sm">
        <div>
          {t('editFieldDrawer:Tool method')}
        </div>
        <div
          className="select w-full rounded-lg border-black/20 btn-sm font-normal justify-between"
          onClick={event => {
            setSelectOperationEl(event.currentTarget)
            setSearchOperationName('')
          }}
        >
          {!toolName && t('operationForm:Select an operation')}
          {toolName}
        </div>
        <OverlayContent
          open={Boolean(selectOperationEl)}
          anchorEl={selectOperationEl}
          onClose={() => setSelectOperationEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Menu className="max-h-[400px]" style={{ width: `${menuWidth}px` }}>
            <div className='px-3 py-2'>
              <SearchInput
                searchName={searchOperationName}
                onNameChange={(name) => { setSearchOperationName(name) }}
              />
              <ToolFormFilteredMenu
                className='pt-2'
                onChange={event => {
                  const name = event.target.value

                  if (name) {
                    setToolName(name)
                    onChange({ ...props.data, method: { name, data: {} } })
                  } else {
                    setToolName(null)
                    onChange({ ...props.data, method: {} })
                  }
                }}
                options={toolNameOptions}
                setSelectEl={setSelectOperationEl}
                searchName={searchOperationName}
              />
            </div>
          </Menu>
        </OverlayContent>
      </div>
      {toolData && toolSchema?.schema && (
        <AutogeneratedForm
          collectionId={props.collectionId}
          fieldId={props.fieldId}
          schema={toolSchema.schema?.inputs ?? {}}
          value={toolData}
          onChange={value => {
            setToolData(value)
            onChange({
              ...props.data,
              method: {
                name: toolName,
                data: value
              }
            })
          }}
          connections={connections}
          eligibleCogfyCreditsProviders={eligibleCogfyCreditsProviders}
          collections={collections}
          fields={props.fields ?? []}
          getConnections={getConnections}
        />
      )}
      {(!toolSchema?.schema) && ToolFormComponent && (
        <ToolFormComponent {...props} />
      )}
    </div>
  )
}
